<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable
      ref="rtable"
      :settings="{hideTable: true, hideExport: true, hideFilter: true, title: t('expirableItemsInsight'), } as any"
      :columns="columnDefs"
      @onDataLoaded="onDataLoaded"
      endpoint="reports/inventory/expirable-items-insight">

      <template #filter-bottom>
        <div class="flex flex-col gap-4 mb-4">
          <!-- Row 1: Location, Period, Date Range -->
          <div class="flex flex-wrap gap-3 items-end">
            <el-select
              filterable
              clearable
              class="flex-1 min-w-[200px]"
              :placeholder="t('selectLocation')"
              v-model="filters.locationID"
            >
              <el-option
                v-for="location in locations"
                :key="location.id"
                :label="$ctx.locale === 'ar' ? location.arName : location.enName"
                :value="location.id"
              ></el-option>
            </el-select>

            <el-select
              class="flex-1 min-w-[200px]"
              :placeholder="t('selectPeriod')"
              v-model="filters.period"
              @change="onPeriodChange"
            >
              <el-option
                v-for="option in periodOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              ></el-option>
            </el-select>

            <el-date-picker
              v-if="filters.period === 'range'"
              class="flex-1 min-w-[200px]"
              v-model="filters.fromDate"
              type="date"
              :placeholder="t('fromDate')"
              value-format="YYYY-MM-DD"
            />

            <el-date-picker
              v-if="filters.period === 'range'"
              class="flex-1 min-w-[200px]"
              v-model="filters.toDate"
              type="date"
              :placeholder="t('toDate')"
              value-format="YYYY-MM-DD"
            />
            <el-select
              filterable
              clearable
              class="flex-[2] min-w-[300px]"
              :placeholder="t('searchItems')"
              value-key="id"
              remote
              :remote-method="searchItems"
              v-model="filters.itemID"
            >
              <el-option
                v-for="option in items"
                :key="option.id"
                :label="option.name"
                :value="option.id"
              ></el-option>
            </el-select>
          </div>

          <!-- Row 2: Item Filter, Safety Settings -->
          <div class="flex flex-wrap gap-3 items-end">

            <div class="min-w-[200px] flex items-center gap-2">
              <span class="text-sm text-gray-700 whitespace-nowrap font-medium">{{ t('expirySafePeriod') }}</span>
              <el-input
                v-model.number="filters.expirySafePeriod"
                type="number"
                style="width: 100px;"
                placeholder="120"
              />
              <span class="text-sm text-gray-400 whitespace-nowrap">{{ t('days') }}</span>
            </div>

            <div class="min-w-[200px] flex items-center gap-2">
              <span class="text-sm text-gray-700 whitespace-nowrap font-medium">{{ t('orderSafePeriod') }}</span>
              <el-input
                v-model.number="filters.orderSafePeriod"
                type="number"
                style="width: 100px;"
                placeholder="150"
              />
              <span class="text-sm text-gray-400 whitespace-nowrap">{{ t('days') }}</span>
            </div>

            <!-- Generate Report Button -->
            <el-button
              type="primary"
              @click="generateReport"
              :loading="isLoading"
            >
              {{ t('generateReport') }}
            </el-button>
          </div>
        </div>
      </template>

      <template #chart>
        <!-- Empty State -->
        <div v-if="processedData.length === 0 && !isLoading" class="flex flex-col items-center justify-center p-16 text-center min-h-[400px]">
          <div class="text-2xl font-semibold text-gray-700 mb-2">{{ t('noDataYet') }}</div>
          <div class="text-base text-gray-500 max-w-md">{{ t('selectFiltersAndGenerate') }}</div>
        </div>

        <!-- Data Loaded State -->
        <div v-else>
          <!-- Metadata Info and Actions -->
          <div class="flex items-center justify-between mb-4 flex-wrap gap-4">
            <div class="bg-gray-50 px-4 py-3 rounded text-sm text-gray-600" v-if="metadata.locationName">
              <span>{{ t('location') }}: {{ metadata.locationName }} | </span>
              <span v-if="metadata.period">{{ t('period') }}: {{ getPeriodLabel(metadata.period) }}</span>
              <span v-if="metadata.periodStart && metadata.periodEnd">
                ({{ formatDate(metadata.periodStart) }} - {{ formatDate(metadata.periodEnd) }})
              </span>
              <span v-if="metadata.generatedAt"> | {{ t('generatedAt') }}: {{ formatDateTime(metadata.generatedAt) }}</span>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-2">
              <el-button @click="expandAll" size="small">{{ t('expandAll') }}</el-button>
              <el-button @click="collapseAll" size="small">{{ t('collapseAll') }}</el-button>
              <el-button @click="exportToExcel" type="primary" size="small">
                <span class="flex items-center gap-1">
                  <span>{{ t('exportExcel') }}</span>
                </span>
              </el-button>
            </div>
          </div>

          <!-- Main Table -->
        <el-table
          :data="processedData"
          style="width: 100%"
          header-row-class-name="reports-table-header"
          :row-class-name="getRowClassName"
          default-expand-all
          ref="mainTable"
          class="expirable-items-table"
        >
          <el-table-column type="expand" width="50">
            <template #default="props">
              <div class="ms-10 bg-gray-50 border-s-4 border-blue-200" v-if="props.row.batches && props.row.batches.length > 0">
                <el-table
                  :data="props.row.batches"
                  style="width: 100%"
                  header-row-class-name="reports-table-header batch-table-header"
                  :row-class-name="getBatchRowClassName"
                  :show-header="true"
                >
                  <el-table-column :label="t('batchNumber')" prop="batchNumber" width="250">
                    <template #default="batchProps">
                      <span class="text-gray-700 font-medium">{{ batchProps.row.batchNumber }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('stock')" prop="stock" width="130">
                    <template #default="batchProps">
                      <span class="font-semibold">{{ formatNumber(batchProps.row.stock) }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('receivingDate')" prop="receivingDate" width="150">
                    <template #default="batchProps">
                      {{ formatDate(batchProps.row.receivingDate) }}
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('agingDays')" prop="agingDays" width="110">
                    <template #default="batchProps">
                      {{ batchProps.row.agingDays }} {{ t('days') }}
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('expiry')" width="160">
                    <template #default="batchProps">
                      <div :class="getExpiryClass(batchProps.row.expiryDays)">
                        <div>{{ formatDate(batchProps.row.expiryDateAdjusted) }}</div>
                        <div class="text-xs opacity-75">
                          {{ formatExpiryDays(batchProps.row.expiryDays) }}
                        </div>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('expectedToSell')" width="160">
                    <template #default="batchProps">
                      <div>{{ formatDate(batchProps.row.expectedToSellDate) }}</div>
                      <div class="text-xs text-gray-500">
                        {{ batchProps.row.expectedToSellDays }} {{ t('days') }}
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('requiredToSell')" width="140">
                    <template #default="batchProps">
                      <span v-if="batchProps.row.requiredToSellQtyPerMonth" class="font-semibold">
                        {{ formatDecimal(batchProps.row.requiredToSellQtyPerMonth) }}/{{ t('month') }}
                      </span>
                      <span v-else>-</span>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="t('itemName')" prop="itemName" width="250" show-overflow-tooltip/>
          <el-table-column :label="t('totalStock')" prop="totalStock" width="130">
            <template #default="props">
              <span class="font-bold text-blue-600">{{ formatNumber(props.row.totalStock) }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="t('monthlyAvgSales')" width="150">
            <template #default="props">
              {{ formatDecimal(props.row.monthlyAvgSales) }}/{{ t('month') }}
            </template>
          </el-table-column>
          <el-table-column label="" width="110">
            <template #default="props">
              <span></span>
            </template>
          </el-table-column>
          <el-table-column label="" width="160">
            <template #default="props">
              <span></span>
            </template>
          </el-table-column>
          <el-table-column :label="t('expectedToSell')" width="160">
            <template #default="props">
              <div>{{ formatDate(props.row.expectedToSellDate) }}</div>
              <div class="text-xs text-gray-500">
                {{ props.row.expectedToSellDays }} {{ t('days') }}
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="t('whenToOrder')" width="160">
            <template #default="props">
              <div :class="getOrderClass(props.row.whenToOrderDays)">
                <div>{{ formatDate(props.row.whenToOrderDate) }}</div>
                <div class="text-xs opacity-75">
                  {{ formatOrderDays(props.row.whenToOrderDays) }}
                </div>
              </div>
            </template>
          </el-table-column>
        </el-table>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useHTTP } from '@/plugins/http'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import { formatPostgresDate } from '@/utils/date'
import Location from '@/models/company/Location'

// i18n
const { t } = useI18n({
  messages: {
    en: {
      selectLocation: 'Select Location',
      selectPeriod: 'Select Period',
      searchItems: 'Search items...',
      expirySafePeriod: 'Expiry Safe Period',
      orderSafePeriod: 'Order Safe Period',
      days: 'days',
      fromDate: 'From Date',
      toDate: 'To Date',
      location: 'Location',
      period: 'Period',
      generatedAt: 'Generated',
      itemName: 'Item Name',
      totalStock: 'Total Stock',
      batchNumber: 'Batch Number',
      stock: 'Stock',
      receivingDate: 'Receiving Date',
      agingDays: 'Aging',
      expiry: 'Expiry',
      monthlyAvgSales: 'Monthly Avg Sales',
      expectedToSell: 'Expected to Sell',
      requiredToSell: 'Required to Sell',
      whenToOrder: 'When to Order',
      month: 'mo',
      expandAll: 'Expand All',
      collapseAll: 'Collapse All',
      exportExcel: 'Export Excel',
      generateReport: 'Generate Report',
      lifetime: 'Lifetime',
      thisYear: 'This Year',
      lastYear: 'Last Year',
      twelveMonths: '12 Months',
      customRange: 'Custom Range',
      daysAgo: 'days ago',
      orderNow: 'ORDER NOW',
      overdue: 'OVERDUE',
      noDataYet: 'No Data Available',
      selectFiltersAndGenerate: 'Please select filters and click "Generate Report"',
      expirableItemsInsight: 'Expirable Items Insight'
    },
    ar: {
      selectLocation: 'اختر الموقع',
      selectPeriod: 'اختر الفترة',
      searchItems: 'البحث عن المواد...',
      expirySafePeriod: 'مدة الأمان لتاريخ للانتهاء',
      orderSafePeriod: 'مدة الأمان للطلب القادم',
      days: 'يوم',
      fromDate: 'من تاريخ',
      toDate: 'إلى تاريخ',
      location: 'الموقع',
      period: 'الفترة',
      generatedAt: 'تم الإنشاء',
      itemName: 'اسم المادة',
      totalStock: 'الكمية الحالية',
      batchNumber: 'رقم الوجبة',
      stock: 'الكمية',
      receivingDate: 'تاريخ الاستلام',
      agingDays: 'العمر',
      expiry: 'تنتهي صلاحتيه في',
      monthlyAvgSales: 'متوسط المبيعات الشهرية',
      expectedToSell: 'متوقع بيعه في',
      requiredToSell: 'المطلوب بيعه',
      whenToOrder: 'الطلب القادم في',
      month: 'شهر',
      expandAll: 'فتح التفاصيل',
      collapseAll: 'غلق التفاصيل',
      exportExcel: 'تصدير Excel',
      generateReport: 'إنشاء التقرير',
      lifetime: 'كل الفترات',
      thisYear: 'هذا العام',
      lastYear: 'العام الماضي',
      twelveMonths: '12 شهر',
      customRange: 'فترة مخصصة',
      daysAgo: 'يوم مضى',
      orderNow: 'اطلب الآن',
      overdue: 'متأخر',
      noDataYet: 'لا توجد بيانات',
      selectFiltersAndGenerate: 'الرجاء تحديد الفلاتر والنقر على "إنشاء التقرير"',
      expirableItemsInsight: 'تقرير المواد المنتهية الصلاحية'
    }
  }
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])
const mainTable = ref()
const http = useHTTP()

const filters = ref({
  locationID: '',
  period: '12_months',
  fromDate: '',
  toDate: '',
  itemID: '',
  expirySafePeriod: 120,
  orderSafePeriod: 150
})

const periodOptions = ref([
  { value: 'lifetime', label: t('lifetime') },
  { value: 'this_year', label: t('thisYear') },
  { value: 'last_year', label: t('lastYear') },
  { value: '12_months', label: t('twelveMonths') },
  // { value: 'range', label: t('customRange') }
])

const locations = ref<Location[]>([])
const items = ref<any[]>([])
const processedData = ref<any[]>([])
const isLoading = ref(false)

const metadata = ref({
  locationID: '',
  locationName: '',
  period: '',
  periodStart: '',
  periodEnd: '',
  daysInPeriod: 0,
  expirySafePeriod: 120,
  orderSafePeriod: 150,
  generatedAt: ''
})

onBeforeMount(async () => {
  columnDefs.value = []
  await loadLocations()
})

async function loadLocations() {
  try {
    const resp = await http.get<Location[]>('/company/locations')
    locations.value = resp
  } catch (error) {
    console.error('Error loading locations:', error)
  }
}


async function searchItems(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const params = new URLSearchParams()
    params.set('q', query)
    
    // Add location_id if selected
    if (rtable.value?.payload?.location_id) {
      params.set('location_id', rtable.value.payload.location_id)
    }
    
    const url = `reports/inventory/expire-item-lookup?${params.toString()}`
    const itemsRes = await http.get<any[]>(url)
    items.value = itemsRes
  } catch (error) {
    console.log(error)
  }
}


function onPeriodChange() {
  if (filters.value.period !== 'range') {
    filters.value.fromDate = ''
    filters.value.toDate = ''
  }
}

async function generateReport() {
  try {
    isLoading.value = true

    // Build query parameters
    const queryParams: any = {
      location_id: filters.value.locationID,
      period: filters.value.period,
      expiry_safe_period: filters.value.expirySafePeriod,
      order_safe_period: filters.value.orderSafePeriod
    }

    // Add date range if custom range is selected
    if (filters.value.period === 'range' && filters.value.fromDate && filters.value.toDate) {
      queryParams.from_date = filters.value.fromDate
      queryParams.to_date = filters.value.toDate
    }

    // Add item filter if specified
    if (filters.value.itemID) {
      queryParams.item_id = filters.value.itemID
    }

    // Trigger API reload with query parameters
    if (rtable.value) {
      rtable.value.payload = { ...queryParams }
      await rtable.value.navigate(rtable.value.payload)
    }
  } catch (error) {
    console.error('Error generating report:', error)
  } finally {
    isLoading.value = false
  }
}

function onDataLoaded(data: any) {
  isLoading.value = false

  if (!data) return

  metadata.value = data.metadata || metadata.value

  // API now returns items with batches already nested
  processedData.value = data.items || []
}

function expandAll() {
  mainTable.value?.store?.states?.data.value.forEach((row: any) => {
    mainTable.value?.toggleRowExpansion(row, true)
  })
}

function collapseAll() {
  mainTable.value?.store?.states?.data.value.forEach((row: any) => {
    mainTable.value?.toggleRowExpansion(row, false)
  })
}

function exportToExcel() {
  if (processedData.value.length === 0) {
    return
  }

  // Create CSV content
  const csvRows: string[] = []

  // Add metadata header
  csvRows.push(`"Expirable Items Insight Report"`)
  csvRows.push(`"Location:","${metadata.value.locationName}"`)
  csvRows.push(`"Period:","${getPeriodLabel(metadata.value.period)}"`)
  if (metadata.value.periodStart && metadata.value.periodEnd) {
    csvRows.push(`"Date Range:","${formatDate(metadata.value.periodStart)} - ${formatDate(metadata.value.periodEnd)}"`)
  }
  csvRows.push(`"Generated At:","${formatDateTime(metadata.value.generatedAt)}"`)
  csvRows.push(`"Expiry Safe Period:","${metadata.value.expirySafePeriod} days"`)
  csvRows.push(`"Order Safe Period:","${metadata.value.orderSafePeriod} days"`)
  csvRows.push('') // Empty row

  // Add table headers (all columns for both items and batches)
  const headers = [
    'Item Name',
    'Stock',
    'Receiving Date',
    'Aging Days',
    'Expiry Date',
    'Expiry Days',
    'Monthly Avg Sales',
    'Expected to Sell Date',
    'Expected to Sell Days',
    'Required to Sell',
    'When to Order Date',
    'When to Order Days'
  ]
  csvRows.push(headers.map(h => `"${h}"`).join(','))

  // Process data - items and batches as rows
  processedData.value.forEach((item: any) => {
    // Parent item row
    const itemRow = [
      `"${item.itemName}"`,
      `"${formatNumber(item.totalStock)}"`, // Total stock for item
      '""', // Receiving Date - empty for item
      '""', // Aging Days - empty for item
      '""', // Expiry Date - empty for item
      '""', // Expiry Days - empty for item
      `"${formatDecimal(item.monthlyAvgSales)}/mo"`,
      `"${formatDate(item.expectedToSellDate)}"`,
      `"${item.expectedToSellDays || '-'}"`,
      '""', // Required to Sell - empty for item
      `"${formatDate(item.whenToOrderDate)}"`,
      `"${formatOrderDays(item.whenToOrderDays)}"`
    ]
    csvRows.push(itemRow.join(','))

    // Batch rows
    if (item.batches && item.batches.length > 0) {
      item.batches.forEach((batch: any) => {
        const batchRow = [
          `"${item.itemName} - ${batch.batchNumber || '-'}"`,
          `"${formatNumber(batch.stock)}"`, // Batch stock
          `"${formatDate(batch.receivingDate)}"`,
          `"${batch.agingDays || '-'}"`,
          `"${formatDate(batch.expiryDateAdjusted)}"`,
          `"${formatExpiryDays(batch.expiryDays)}"`,
          // `"${formatDecimal(batch.monthlyAvgSales)}/mo"`,
          `"${formatDate(batch.expectedToSellDate)}"`,
          `"${batch.expectedToSellDays || '-'}"`,
          `"${batch.requiredToSellQtyPerMonth ? formatDecimal(batch.requiredToSellQtyPerMonth) + '/mo' : '-'}"`,
          // `"${formatDate(batch.whenToOrderDate)}"`,
          // `"${formatOrderDays(batch.whenToOrderDays)}"`
        ]
        csvRows.push(batchRow.join(','))
      })
    }
  })

  // Create and download file
  const csvContent = csvRows.join('\n')
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  const fileName = `Expirable_Items_Report_${metadata.value.locationName}_${new Date().toISOString().split('T')[0]}.csv`

  link.setAttribute('href', url)
  link.setAttribute('download', fileName)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function getRowClassName() {
  return 'parent-row'
}

function getBatchRowClassName() {
  return 'batch-row'
}

function getExpiryClass(days: number | null) {
  if (days === null) return ''
  if (days < 0) return 'text-red-600 font-bold'
  if (days <= 30) return 'text-orange-600 font-bold'
  return ''
}

function getOrderClass(days: number | null) {
  if (days === null) return ''
  if (days < 0) return 'text-red-600 font-bold'
  if (days <= 30) return 'text-orange-600 font-bold'
  return ''
}

function formatNumber(value: number | null): string {
  if (value === null) return '-'
  return value.toLocaleString()
}

function formatDecimal(value: number | null): string {
  if (value === null) return '-'
  return value.toFixed(1)
}

function formatDate(date: string | null): string {
  if (!date) return '-'
  return formatPostgresDate(date, 'DD/MM/YYYY')
}

function formatDateTime(date: string | null): string {
  if (!date) return '-'
  return formatPostgresDate(date, 'DD/MM/YYYY hh:mm A')
}

function formatExpiryDays(days: number | null): string {
  if (days === null) return '-'
  if (days < 0) return `${Math.abs(days)} ${t('daysAgo')}`
  return `${days} ${t('days')}`
}

function formatOrderDays(days: number | null): string {
  if (days === null) return '-'
  if (days < 0) return t('orderNow')
  return `${days} ${t('days')}`
}

function getPeriodLabel(period: string): string {
  const option = periodOptions.value.find(o => o.value === period)
  return option ? option.label : period
}
</script>

<style scoped>
/* Table Deep Selectors for Element Plus */
:deep(.parent-row) {
  background-color: #fef9e7;
  font-weight: bold;
}

:deep(.parent-row td) {
  font-size: 15px;
  padding: 12px 8px;
}

:deep(.batch-table-header) {
  background-color: #F3F4F6 !important;
  font-size: 13px;
}

:deep(.batch-row) {
  background-color: #FFFFFF;
}

:deep(.batch-row td) {
  padding: 10px 8px;
  border-bottom: 1px solid #E5E7EB;
}

:deep(.batch-row:hover) {
  background-color: #F9FAFB !important;
}

/* Hide default ReportsTable filter */
:deep(.reports-table-filter) {
  display: none !important;
}

/* Responsive Table */
@media (max-width: 768px) {
  :deep(.parent-row td) {
    font-size: 14px;
    padding: 10px 6px;
  }

  :deep(.batch-row td) {
    padding: 8px 6px;
    font-size: 13px;
  }
}
</style>
